import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle2, Download, Mail, Send, ShieldCheck, Upload, UserPlus, Copy,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const btn = "hard-btn py-2 px-4 font-mono uppercase text-[10px] tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-40";
const outline = "border-2 border-ink py-2 px-4 font-mono uppercase text-[10px] tracking-widest inline-flex items-center justify-center gap-2 hover:bg-ink hover:text-white disabled:opacity-40";
const input = "w-full px-3 py-2 border-2 border-ink font-mono text-sm";

const dot = { ok: "bg-green-600", warning: "bg-yellow-500", blocked: "bg-red-600" };

const save = (data, name, type) => {
  const url = URL.createObjectURL(new Blob([data], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export default function Form1099Ops({ year }) {
  const [pre, setPre] = useState(null);
  const [del, setDel] = useState(null);
  const [subs, setSubs] = useState([]);
  const [invites, setInvites] = useState([]);
  const [busy, setBusy] = useState("");
  const [invite, setInvite] = useState({ email: "", name: "", note: "" });

  const load = useCallback(async () => {
    const [p, d, s, i] = await Promise.all([
      api.get(`/1099/precheck?year=${year}`).catch(() => null),
      api.get(`/1099/delivery?year=${year}`).catch(() => null),
      api.get(`/w9/submissions?status=pending`).catch(() => ({ data: [] })),
      api.get(`/w9/invites`).catch(() => ({ data: [] })),
    ]);
    setPre(p?.data || null);
    setDel(d?.data || null);
    setSubs(s?.data || []);
    setInvites(i?.data || []);
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const act = async (label, fn) => {
    setBusy(label);
    try { await fn(); } catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };

  const downloadRequest = () => act("request", async () => {
    const r = await api.get(`/1099/tin-match-request.txt?year=${year}`, { responseType: "blob" });
    save(r.data, `tin-match-request-${year}.txt`, "text/plain");
    toast.success("IRS bulk TIN matching request file ready");
  });

  const importResponse = (file) => act("import", async () => {
    const text = await file.text();
    const r = await api.post(`/1099/tin-match-response?year=${year}`, text, {
      headers: { "Content-Type": "text/plain" },
    });
    toast.success(`${r.data.matched} matched · ${r.data.mismatched} mismatched`);
    await load();
  });

  const sendAll = () => act("send", async () => {
    const r = await api.post(`/1099/delivery/send`, { year: Number(year) });
    const { sent = [], consent_requested = [], failed = [] } = r.data;
    toast.success(`${sent.length} form(s) sent · ${consent_requested.length} consent request(s)`);
    if (failed.length) toast.error(`${failed.length} could not be emailed: ${failed[0].reason}`);
    await load();
  });

  const sendOne = (id) => act(`send-${id}`, async () => {
    await api.post(`/1099/delivery/send`, { year: Number(year), employee_ids: [id] });
    toast.success("Sent");
    await load();
  });

  const paperConsent = (id, consented) => act(`consent-${id}`, async () => {
    await api.post(`/1099/delivery/consent/${id}`, { year: Number(year), consented });
    toast.success(consented ? "Paper consent recorded" : "Consent withdrawn");
    await load();
  });

  const setPolicy = (patch) => act("policy", async () => {
    await api.put(`/1099/delivery/policy`, { ...del.policy, ...patch });
    await load();
  });

  const createInvite = () => act("invite", async () => {
    const r = await api.post(`/w9/invites`, invite);
    toast.success(r.data.emailed ? "W-9 request emailed" : "Invite created — email did not send");
    setInvite({ email: "", name: "", note: "" });
    await load();
  });

  const decide = (id, verdict) => act(`sub-${id}`, async () => {
    await api.post(`/w9/submissions/${id}/${verdict}`);
    toast.success(verdict === "approve" ? "Contractor added to the roster" : "Submission rejected");
    await load();
  });

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Link copied");
  };

  const irs = pre?.irs_results;

  return (
    <div className="space-y-8" data-testid="form1099-ops">
      {/* ------------------------------------------------ 1 · PRECHECK */}
      <div className="hard-card p-6" data-testid="tin-precheck-card">
        <div className="overline mb-2">§ TIN MATCH PRECHECK · {year}</div>
        <div className="font-heading font-black text-2xl tracking-tight">
          Catch a bad name/TIN pair before the IRS does
        </div>
        <div className="font-mono text-xs text-muted-foreground mt-1 max-w-2xl">
          Every reportable recipient is checked for a nine-digit tax ID, a derivable IRS name
          control, a duplicate TIN and a complete address. IRS TIN Matching itself runs through
          e-Services, so the request file is built here and the results file is imported back.
        </div>

        {!pre || !pre.total ? (
          <div className="mt-4 font-mono text-xs" data-testid="precheck-empty">
            No reportable 1099 recipient for {year}.
          </div>
        ) : (
          <>
            <div className="mt-4 grid grid-cols-3 gap-3 font-mono text-xs" data-testid="precheck-counts">
              <div className="border-2 border-ink p-3">
                <div className="text-2xl font-black">{pre.total}</div>recipients
              </div>
              <div className="border-2 border-ink p-3">
                <div className="text-2xl font-black text-red-700">{pre.blocked}</div>blockers
              </div>
              <div className="border-2 border-ink p-3">
                <div className="text-2xl font-black">{pre.warnings}</div>warnings
              </div>
            </div>

            <div className="mt-4 space-y-2" data-testid="precheck-rows">
              {pre.recipients.map((r) => (
                <div key={r.id} className="border-2 border-ink p-3" data-testid={`precheck-row-${r.id}`}>
                  <div className="flex flex-wrap items-center gap-2 font-mono text-xs">
                    <span className={`w-2 h-2 ${dot[r.status]} inline-block`} />
                    <span className="font-bold">{r.name}</span>
                    <span className="opacity-60">
                      {r.tin_type === "ein" ? "EIN" : "SSN/ITIN"} ••{r.tin_last4 || "??"}
                    </span>
                    <span className="opacity-60">name control {r.name_control || "—"}</span>
                  </div>
                  {r.issues.map((i, n) => (
                    <div key={n} className={`mt-1 font-mono text-[10px] flex gap-2 ${i.severity === "blocker" ? "text-red-700" : "opacity-70"}`}>
                      <AlertTriangle className="w-3 h-3 shrink-0 mt-[2px]" />{i.message}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={downloadRequest} className={btn} data-testid="tin-match-request-download"
                disabled={busy === "request"}>
                <Download className="w-3 h-3" /> IRS bulk request file
              </button>
              <label className={outline} data-testid="tin-match-response-upload">
                <Upload className="w-3 h-3" /> Import IRS results
                <input type="file" accept=".txt,.csv" className="hidden"
                  onChange={(e) => e.target.files?.[0] && importResponse(e.target.files[0])} />
              </label>
            </div>

            {irs ? (
              <div className="mt-4 border-2 border-ink p-3" data-testid="tin-match-results">
                <div className="overline text-[10px] mb-2">
                  IRS results imported {String(irs.imported_at).slice(0, 10)} · {irs.matched} matched ·
                  {" "}{irs.mismatched} mismatched · {irs.errors} error(s)
                </div>
                {irs.rows.filter((r) => r.result !== "match").map((r, n) => (
                  <div key={n} className="font-mono text-[10px] text-red-700 flex gap-2">
                    <AlertTriangle className="w-3 h-3 shrink-0 mt-[2px]" />
                    {r.name} (••{r.tin_last4}) — code {r.code}: {r.message}
                  </div>
                ))}
                {irs.mismatched + irs.errors === 0 ? (
                  <div className="font-mono text-[10px] flex gap-2">
                    <CheckCircle2 className="w-3 h-3 shrink-0 mt-[2px]" />
                    Every submitted name/TIN pair matched IRS records.
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="mt-3 font-mono text-[10px] opacity-70">{pre.note}</div>
          </>
        )}
      </div>

      {/* ------------------------------------------------ 2 · DELIVERY */}
      <div className="hard-card p-6" data-testid="delivery-card">
        <div className="overline mb-2">§ 1099 ELECTRONIC DELIVERY · {year}</div>
        <div className="font-heading font-black text-2xl tracking-tight">
          Furnish each copy and see who opened it
        </div>
        <div className="font-mono text-xs text-muted-foreground mt-1 max-w-2xl">
          Each recipient gets their own secure link rather than a tax form attached to an email.
          Opens and downloads are logged. Electronic furnishing requires the recipient's
          affirmative consent, which is enforced by default.
        </div>

        {del ? (
          <>
            <div className="mt-4 flex flex-wrap gap-4 font-mono text-xs items-center">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="policy-consent-toggle">
                <input type="checkbox" checked={del.policy.consent_required}
                  onChange={(e) => setPolicy({ consent_required: e.target.checked })} />
                Require recorded consent before emailing
              </label>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="policy-autoapprove-toggle">
                <input type="checkbox" checked={del.policy.w9_auto_approve}
                  onChange={(e) => setPolicy({ w9_auto_approve: e.target.checked })} />
                Auto-approve submitted W-9s
              </label>
            </div>

            {!del.recipients.length ? (
              <div className="mt-4 font-mono text-xs" data-testid="delivery-empty">
                No reportable 1099 recipient for {year}.
              </div>
            ) : (
              <>
                <div className="mt-4 space-y-2" data-testid="delivery-rows">
                  {del.recipients.map((r) => (
                    <div key={r.employee_id} className="border-2 border-ink p-3 flex flex-wrap gap-3 items-center justify-between"
                      data-testid={`delivery-row-${r.employee_id}`}>
                      <div className="font-mono text-xs">
                        <div className="font-bold">{r.name}</div>
                        <div className="opacity-60">{r.email || "no email on file"}</div>
                        <div className="opacity-60">
                          {r.consented ? `consent ${r.consent_method} ${String(r.consent_at || "").slice(0, 10)}` : "no consent on record"}
                          {" · "}{r.send_count} send(s) · {r.open_count} open(s) · {r.download_count} download(s)
                          {r.last_opened_at ? ` · last opened ${String(r.last_opened_at).slice(0, 16).replace("T", " ")}` : ""}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button className={outline} data-testid={`delivery-send-${r.employee_id}`}
                          disabled={busy === `send-${r.employee_id}`} onClick={() => sendOne(r.employee_id)}>
                          <Mail className="w-3 h-3" /> {r.send_count ? "Resend" : "Send"}
                        </button>
                        <button className={outline} data-testid={`delivery-consent-${r.employee_id}`}
                          disabled={busy === `consent-${r.employee_id}`}
                          onClick={() => paperConsent(r.employee_id, !r.consented)}>
                          <ShieldCheck className="w-3 h-3" /> {r.consented ? "Withdraw" : "Paper consent"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <button onClick={sendAll} className={`${btn} mt-4`} data-testid="delivery-send-all"
                  disabled={busy === "send"}>
                  <Send className="w-3 h-3" /> {busy === "send" ? "Sending…" : "Send to every recipient"}
                </button>
              </>
            )}
          </>
        ) : null}
      </div>

      {/* ------------------------------------------------ 3 · W-9 INTAKE */}
      <div className="hard-card p-6" data-testid="w9-intake-card">
        <div className="overline mb-2">§ CONTRACTOR SELF-ONBOARDING</div>
        <div className="font-heading font-black text-2xl tracking-tight">
          Let the contractor key their own EIN
        </div>
        <div className="font-mono text-xs text-muted-foreground mt-1 max-w-2xl">
          Send a tokenized W-9 link. The contractor supplies their legal name, entity
          classification, tax ID and address, certifies it and signs electronically. Submissions
          wait in the queue below unless auto-approve is on.
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <input className={input} placeholder="Contractor email" data-testid="w9-invite-email"
            value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} />
          <input className={input} placeholder="Name (optional)" data-testid="w9-invite-name"
            value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} />
          <input className={input} placeholder="Note (optional)" data-testid="w9-invite-note"
            value={invite.note} onChange={(e) => setInvite({ ...invite, note: e.target.value })} />
        </div>
        <button onClick={createInvite} className={`${btn} mt-3`} data-testid="w9-invite-send"
          disabled={busy === "invite" || !invite.email}>
          <UserPlus className="w-3 h-3" /> {busy === "invite" ? "Sending…" : "Send W-9 request"}
        </button>

        {subs.length ? (
          <div className="mt-5 space-y-2" data-testid="w9-submissions">
            <div className="overline text-[10px]">Pending submissions · {subs.length}</div>
            {subs.map((s) => (
              <div key={s.id} className="border-2 border-ink p-3 flex flex-wrap gap-3 items-center justify-between"
                data-testid={`w9-sub-${s.id}`}>
                <div className="font-mono text-xs">
                  <div className="font-bold">{s.business_name || `${s.first_name} ${s.last_name}`}</div>
                  <div className="opacity-60">
                    {s.entity_label} · {s.tin_type === "ein" ? "EIN" : "SSN/ITIN"} {s.tin_masked} ·
                    {" "}name control {s.name_control}
                  </div>
                  <div className="opacity-60">
                    {s.address}, {s.city} {s.state} {s.zip_code} · signed {s.signature_name}
                    {s.e_delivery_consent ? " · e-delivery consent given" : ""}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button className={btn} data-testid={`w9-approve-${s.id}`}
                    disabled={busy === `sub-${s.id}`} onClick={() => decide(s.id, "approve")}>
                    <CheckCircle2 className="w-3 h-3" /> Approve
                  </button>
                  <button className={outline} data-testid={`w9-reject-${s.id}`}
                    disabled={busy === `sub-${s.id}`} onClick={() => decide(s.id, "reject")}>
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-5 font-mono text-xs" data-testid="w9-submissions-empty">
            No W-9 submission is waiting for review.
          </div>
        )}

        {invites.length ? (
          <div className="mt-5" data-testid="w9-invites">
            <div className="overline text-[10px] mb-2">Sent requests</div>
            {invites.slice(0, 10).map((i) => (
              <div key={i.id} className="font-mono text-[11px] flex flex-wrap gap-2 items-center py-1"
                data-testid={`w9-invite-${i.id}`}>
                <span className="font-bold">{i.email}</span>
                <span className="opacity-60">{i.status}</span>
                <button onClick={() => copy(i.link)} className="underline inline-flex items-center gap-1"
                  data-testid={`w9-invite-copy-${i.id}`}>
                  <Copy className="w-3 h-3" /> copy link
                </button>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
